import React, { useEffect, useState } from 'react';
import { getAnnouncements } from '../services/api';
import { Box, Typography, List, ListItem, Divider } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

interface Announcement {
    id: number;
    title: string;
    message: string;
    created_at: string;
}

interface UserAnnouncement {
    id: number;
    announcement: Announcement;
    is_read: boolean;
    read_at: string | null;
}

const AnnouncementList: React.FC = () => {
    const { token, refreshTokenFunction, logout } = useAuth();
    const [announcements, setAnnouncements] = useState<UserAnnouncement[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchAnnouncements = async () => {
            if (token) {
                try {
                    const response = await getAnnouncements(token);
                    setAnnouncements(response.data);
                } catch (error: any) {
                    // 401の場合はリフレッシュトークンで再取得を試みる
                    if (error.response && error.response.status === 401) {
                        try {
                            const newToken = await refreshTokenFunction();
                            if (newToken) {
                                const retryResponse = await getAnnouncements(newToken);
                                setAnnouncements(retryResponse.data);
                                return;
                            }
                        } catch (refreshError) {}
                    }
                    setError('お知らせの取得に失敗しました。');
                    logout();
                }
            }
        };

        fetchAnnouncements();
    }, [token, refreshTokenFunction, logout]);

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }

    return (
        <Box sx={{ marginTop: 6, marginBottom: 6, maxHeight: '90vh', overflowY: 'auto' }}>
            <Typography variant="h5" gutterBottom>
                お知らせ一覧
            </Typography>
            <List>
                {announcements.map((userAnnouncement, index) => (
                    <React.Fragment key={userAnnouncement.id}>
                        <ListItem
                            sx={{
                                paddingY: 2,
                                width: '100%',
                                margin: '0 auto',
                            }}>
                            <Box>
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', marginBottom: 0.5 }}>
                                    {new Date(userAnnouncement.announcement.created_at).toLocaleDateString()}
                                </Typography>
                                <Typography variant="h6">{userAnnouncement.announcement.title}</Typography>
                                <div
                                    dangerouslySetInnerHTML={{ __html: userAnnouncement.announcement.message }}
                                />
                            </Box>
                        </ListItem>
                        {index < announcements.length - 1 && (
                            <Divider
                                sx={{
                                    borderStyle: 'dotted',
                                    width: '90%',
                                    margin: '0 auto',
                                    borderColor: 'gray',
                                    borderWidth: '1px',
                                }}
                            />
                        )}
                    </React.Fragment>
                ))}
            </List>
        </Box>
    );
};

export default AnnouncementList;
